import { BodyText, Skeleton } from "@/components/ui";
import { formatDateTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useMemo, useState } from "react";
import { useResourceHistory } from "../network/useAdminFhir";
import { VersionDiffView } from "./VersionDiffView";

interface VersionHistoryProps {
  resourceType: string;
  resourceId: string;
}

/**
 * Version-history timeline. Scrub across versions to visualize what changed
 * between each version and its predecessor. Each change is shown with both a
 * clinical and an engineering column (see VersionDiffView).
 */
export const VersionHistory = ({
  resourceType,
  resourceId,
}: VersionHistoryProps) => {
  const {
    data: versions,
    isLoading,
    isError,
  } = useResourceHistory(resourceType, resourceId);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const reduceMotion = useReducedMotion();

  // Timeline runs oldest → newest, left → right.
  const timeline = useMemo(
    () => (versions ? [...versions].reverse() : []),
    [versions],
  );

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (isError || !versions || versions.length === 0) {
    return (
      <BodyText size="sm" textColor="secondary">
        No version history available for this resource.
      </BodyText>
    );
  }

  // Default selection = newest version (index 0 of the newest-first list).
  const selectedIndex = Math.max(
    0,
    versions.findIndex((version) => version.versionId === selectedVersionId),
  );
  const selected = versions[selectedIndex];
  const predecessor = versions[selectedIndex + 1];

  return (
    <div className="space-y-4">
      {/* Scrubber */}
      <div className="flex items-stretch gap-2 overflow-x-auto pb-2">
        {timeline.map((version) => {
          const isActive = version.versionId === selected.versionId;
          return (
            <button
              key={version.versionId}
              type="button"
              onClick={() => setSelectedVersionId(version.versionId)}
              className={cn(
                "relative flex min-w-[7rem] flex-col rounded-md border p-2 text-left transition-colors",
                isActive ? "border-link" : "border-border hover:bg-muted",
              )}
            >
              {isActive && (
                <motion.div
                  layoutId="version-scrubber-active"
                  className="absolute inset-0 rounded-md bg-info/10"
                  transition={{ duration: reduceMotion ? 0 : 0.2 }}
                />
              )}
              <BodyText size="sm" bold className="relative z-10">
                v{version.versionId}
              </BodyText>
              <BodyText
                size="xs"
                textColor="secondary"
                className="relative z-10"
              >
                {version.lastUpdated
                  ? formatDateTime(version.lastUpdated)
                  : "—"}
              </BodyText>
            </button>
          );
        })}
      </div>

      <div className="rounded-md border p-4">
        <BodyText size="sm" textColor="secondary" className="mb-3">
          {predecessor
            ? `Changes from v${predecessor.versionId} → v${selected.versionId}`
            : `v${selected.versionId} (initial version)`}
        </BodyText>

        <AnimatePresence mode="wait">
          <motion.div
            key={selected.versionId}
            initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduceMotion ? 0 : -8 }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
          >
            <VersionDiffView
              before={predecessor?.resource}
              after={selected.resource}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
